import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  Calendar,
  Clock,
  ArrowLeft,
  Tag,
  User,
  Facebook,
  Twitter,
  Linkedin,
  Link as LinkIcon,
  ChevronRight,
  FileText,
  Loader2,
  Eye
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import { toast } from "@/utils/toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Check if content is HTML (from WYSIWYG editor) or Markdown
const isHtmlContent = (content) => {
  if (!content) return false;
  // Check for common HTML tags
  return /<[a-z][\s\S]*>/i.test(content) &&
         (content.includes('<p>') || content.includes('<h1>') || content.includes('<h2>') || content.includes('<div>'));
};

// Generate table of contents from HTML content
const extractHeadingsFromHtml = (html) => {
  const headings = [];
  if (!html) return headings;

  // Parse HTML to extract h2 and h3 headings
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const allHeadings = [...doc.querySelectorAll('h2, h3')];

  allHeadings.forEach((heading) => {
    const text = heading.textContent || '';
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    headings.push({
      level: heading.tagName === 'H2' ? 2 : 3,
      text,
      id
    });
  });

  return headings;
};

// Generate table of contents from markdown (legacy support)
const extractHeadingsFromMarkdown = (markdown) => {
  const headings = [];
  const lines = markdown.split('\n');

  lines.forEach((line, index) => {
    const h2Match = line.match(/^## (.+)$/);
    const h3Match = line.match(/^### (.+)$/);

    if (h2Match) {
      const text = h2Match[1].replace(/\*\*/g, '');
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      headings.push({ level: 2, text, id });
    } else if (h3Match) {
      const text = h3Match[1].replace(/\*\*/g, '');
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      headings.push({ level: 3, text, id });
    }
  });

  return headings;
};

// Round bordered icon button used for the share row
const shareBtnClass = "w-10 h-10 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:border-slate-400 hover:text-slate-900 transition-colors";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    loadPost();
    loadCategories();
  }, [slug]);

  const loadPost = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/blog/posts/${slug}`);
      const data = await response.json();

      if (data.success) {
        setPost(data.post);
        setRelated(data.related || []);
      } else {
        navigate("/blog");
      }
    } catch (error) {
      console.error("Error loading post:", error);
      navigate("/blog");
    } finally {
      setIsLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/blog/categories`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  // Determine if content is HTML or Markdown and extract headings accordingly
  const headings = useMemo(() => {
    if (!post?.content) return [];
    if (isHtmlContent(post.content)) {
      return extractHeadingsFromHtml(post.content);
    }
    return extractHeadingsFromMarkdown(post.content);
  }, [post?.content]);

  // Process content to add IDs to headings for anchor links
  const processedContent = useMemo(() => {
    if (!post?.content) return '';
    if (!isHtmlContent(post.content)) return post.content;

    // Add IDs to h2 and h3 elements for anchor links
    let content = post.content;
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');

    doc.querySelectorAll('h2, h3').forEach((heading) => {
      const text = heading.textContent || '';
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      heading.id = id;
      heading.className = 'scroll-mt-20';
    });

    return doc.body.innerHTML;
  }, [post?.content]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleShare = (platform) => {
    const title = post?.title || '';
    let url = '';

    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(title)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied to clipboard!");
        return;
    }

    if (url) {
      window.open(url, '_blank', 'width=600,height=400');
    }
  };

  if (isLoading) {
    return (
      <>
        <Header title="MintSlip" />
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
        <Footer />
      </>
    );
  }

  if (!post) {
    return null;
  }

  // Schema.org structured data
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.metaDescription,
    "image": post.featuredImage ? (post.featuredImage.startsWith('/') ? `https://mintslip.com${post.featuredImage}` : post.featuredImage) : "https://mintslip.com/og-image.png",
    "author": {
      "@type": "Organization",
      "name": post.author || "MintSlip Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": "MintSlip",
      "logo": {
        "@type": "ImageObject",
        "url": "https://mintslip.com/mintslip-logo.png"
      }
    },
    "datePublished": post.publishDate,
    "dateModified": post.updatedAt || post.publishDate
  };

  const faqSchema = post.faqSchema?.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": post.faqSchema.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://mintslip.com" },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://mintslip.com/blog" },
      { "@type": "ListItem", "position": 3, "name": post.title, "item": `https://mintslip.com/blog/${post.slug}` }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{post.metaTitle || post.title} | MintSlip Blog</title>
        <meta name="description" content={post.metaDescription || post.excerpt} />
        <meta name="robots" content={post.indexFollow ? "index, follow" : "noindex, nofollow"} />
        <link rel="canonical" href={`https://mintslip.com/blog/${post.slug}`} />

        {/* Open Graph */}
        <meta property="og:title" content={post.metaTitle || post.title} />
        <meta property="og:description" content={post.metaDescription || post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://mintslip.com/blog/${post.slug}`} />
        <meta property="og:image" content={post.featuredImage ? (post.featuredImage.startsWith('/') ? `https://mintslip.com${post.featuredImage}` : post.featuredImage) : 'https://mintslip.com/og-image.png'} />
        <meta property="og:site_name" content="MintSlip" />
        <meta property="article:published_time" content={post.publishDate} />
        <meta property="article:author" content={post.author} />

        {/* Twitter */}
        <meta name="twitter:card" content={post.featuredImage ? "summary_large_image" : "summary"} />
        <meta name="twitter:title" content={post.metaTitle || post.title} />
        <meta name="twitter:description" content={post.metaDescription || post.excerpt} />
        <meta name="twitter:image" content={post.featuredImage ? (post.featuredImage.startsWith('/') ? `https://mintslip.com${post.featuredImage}` : post.featuredImage) : 'https://mintslip.com/og-image.png'} />

        {/* Schema */}
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>

      {/* overflowX clip matches the home page root — keeps the sticky navbar
          pill from causing a horizontal page scroll on small phones. */}
      <div className="min-h-screen bg-white relative" style={{ overflowX: "clip" }}>
      <Header title="MintSlip" />

      {/* Breadcrumbs */}
      <div className="max-w-[1288px] mx-auto px-6 pt-6">
        <nav className="flex items-center gap-2 text-sm text-slate-500">
          <Link to="/" className="hover:text-emerald-700 transition-colors">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/blog" className="hover:text-emerald-700 transition-colors">Blog</Link>
          {post.category && (
            <>
              <ChevronRight className="w-4 h-4" />
              <Link to={`/blog?category=${post.category}`} className="hover:text-emerald-700 transition-colors">
                {categories.find(c => c.slug === post.category)?.name || post.category}
              </Link>
            </>
          )}
          <ChevronRight className="w-4 h-4" />
          <span className="text-slate-700 truncate max-w-[200px]">{post.title}</span>
        </nav>
      </div>

      <article className="max-w-[1288px] mx-auto px-6 py-10 md:py-12">
        {/* Header */}
        <header className="mb-8">
          {post.category && (
            <Link
              to={`/blog?category=${post.category}`}
              className="inline-block px-3.5 py-1.5 bg-emerald-50 border border-emerald-200/80 text-emerald-900 text-sm font-medium rounded-full mb-5 hover:bg-emerald-100 transition-colors"
            >
              {categories.find(c => c.slug === post.category)?.name || post.category}
            </Link>
          )}

          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.12 }}>
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-slate-500">
            <span className="flex items-center gap-1.5">
              <User className="w-4 h-4" />
              {post.author || "MintSlip Team"}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {formatDate(post.publishDate)}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {post.readingTime || 5} min read
            </span>
            <span className="flex items-center gap-1.5">
              <Eye className="w-4 h-4" />
              {post.views || 0} views
            </span>
          </div>
        </header>

        {/* Featured Image */}
        {post.featuredImage && (
          <div className="mb-10 rounded-[28px] overflow-hidden max-h-[420px] bg-slate-100">
            <img
              src={post.featuredImage.startsWith('/') ? `${BACKEND_URL}${post.featuredImage}` : post.featuredImage}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Content Layout */}
        <div className="lg:flex lg:gap-12">
          {/* Table of Contents - Sidebar */}
          {headings.length > 0 && (
            <aside className="hidden lg:block lg:w-72 flex-shrink-0">
              <div className="sticky top-24 bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
                <h4 className="font-display text-sm font-bold text-slate-900 uppercase tracking-[0.15em] mb-4">On this page</h4>
                <nav className="space-y-2.5">
                  {headings.map((heading, index) => (
                    <a
                      key={index}
                      href={`#${heading.id}`}
                      className={`block text-sm text-slate-600 hover:text-emerald-700 transition-colors leading-snug ${
                        heading.level === 3 ? 'pl-4' : ''
                      }`}
                    >
                      {heading.text}
                    </a>
                  ))}
                </nav>

                {/* Share Buttons */}
                <div className="mt-6 pt-5 border-t border-slate-200">
                  <h4 className="font-display text-sm font-bold text-slate-900 uppercase tracking-[0.15em] mb-4">Share</h4>
                  <div className="flex gap-2">
                    <button className={shareBtnClass} aria-label="Share on Facebook" onClick={() => handleShare('facebook')}>
                      <Facebook className="w-4 h-4" />
                    </button>
                    <button className={shareBtnClass} aria-label="Share on Twitter" onClick={() => handleShare('twitter')}>
                      <Twitter className="w-4 h-4" />
                    </button>
                    <button className={shareBtnClass} aria-label="Share on LinkedIn" onClick={() => handleShare('linkedin')}>
                      <Linkedin className="w-4 h-4" />
                    </button>
                    <button className={shareBtnClass} aria-label="Copy link" onClick={() => handleShare('copy')}>
                      <LinkIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </aside>
          )}

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Mobile TOC */}
            {headings.length > 0 && (
              <div className="lg:hidden mb-8 p-6 bg-slate-100 rounded-2xl">
                <h4 className="font-display text-sm font-bold text-slate-900 uppercase tracking-[0.15em] mb-4">On this page</h4>
                <nav className="space-y-2.5">
                  {headings.map((heading, index) => (
                    <a
                      key={index}
                      href={`#${heading.id}`}
                      className={`block text-sm text-slate-600 hover:text-emerald-700 leading-snug ${
                        heading.level === 3 ? 'pl-4' : ''
                      }`}
                    >
                      {heading.text}
                    </a>
                  ))}
                </nav>
              </div>
            )}

            {/* Article Content */}
            <div
              className="blog-article"
              dangerouslySetInnerHTML={{ __html: processedContent }}
            />

            {/* CTA Block — black tone card with the gradient pill */}
            <div className="my-12 rounded-[28px] bg-[#0b0b0b] px-6 py-10 md:px-10 md:py-12 text-center">
              <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-white mb-3">
                Create documents in <span className="font-black">minutes</span>
              </h3>
              <p className="text-slate-400 mb-6 max-w-md mx-auto">
                Accurate, professional documents with instant download. No sign-up required.
              </p>
              <div className="flex justify-center">
                <button className="navbar-cta" onClick={() => navigate("/app")}>
                  <FileText size={18} />
                  <span>Create a document</span>
                </button>
              </div>
            </div>

            {/* FAQ Section */}
            {post.faqSchema?.length > 0 && (
              <div className="my-12">
                <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 mb-6">
                  Frequently asked <span className="font-black">questions</span>
                </h2>
                <div className="space-y-4">
                  {post.faqSchema.map((faq, index) => (
                    <div key={index} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
                      <h3 className="font-display font-bold text-slate-900 mb-2">{faq.question}</h3>
                      <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {post.tags?.length > 0 && (
              <div className="mt-8 pt-6 border-t border-slate-200">
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag className="w-4 h-4 text-slate-400" />
                  {post.tags.map((tag, index) => (
                    <Link
                      key={index}
                      to={`/blog?tag=${encodeURIComponent(tag)}`}
                      className="px-3.5 py-1.5 bg-slate-100 text-slate-600 text-sm font-medium rounded-full hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Mobile Share */}
            <div className="lg:hidden mt-8 pt-6 border-t border-slate-200">
              <h4 className="font-display text-sm font-bold text-slate-900 uppercase tracking-[0.15em] mb-4">Share</h4>
              <div className="flex gap-2">
                <button className={shareBtnClass} aria-label="Share on Facebook" onClick={() => handleShare('facebook')}>
                  <Facebook className="w-4 h-4" />
                </button>
                <button className={shareBtnClass} aria-label="Share on Twitter" onClick={() => handleShare('twitter')}>
                  <Twitter className="w-4 h-4" />
                </button>
                <button className={shareBtnClass} aria-label="Share on LinkedIn" onClick={() => handleShare('linkedin')}>
                  <Linkedin className="w-4 h-4" />
                </button>
                <button className={shareBtnClass} aria-label="Copy link" onClick={() => handleShare('copy')}>
                  <LinkIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {related.length > 0 && (
        <section className="max-w-[1288px] mx-auto px-6 py-12 md:py-16">
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 mb-8">
            Related <span className="font-black">articles</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((relPost) => (
              <Link
                key={relPost.id}
                to={`/blog/${relPost.slug}`}
                className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-shadow group"
              >
                {relPost.featuredImage ? (
                  <div className="aspect-video bg-slate-100 overflow-hidden">
                    <img
                      src={relPost.featuredImage.startsWith('/') ? `${BACKEND_URL}${relPost.featuredImage}` : relPost.featuredImage}
                      alt={relPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-slate-100 flex items-center justify-center">
                    <span className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center">
                      <FileText className="w-7 h-7 text-slate-900" />
                    </span>
                  </div>
                )}
                <div className="p-5">
                  <h3 className="font-display font-bold tracking-tight text-slate-900 line-clamp-2 group-hover:text-emerald-700 transition-colors">
                    {relPost.title}
                  </h3>
                  <p className="text-sm text-slate-500 mt-2 flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {relPost.readingTime || 5} min read
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Back to Blog */}
      <div className="max-w-[1288px] mx-auto px-6 pb-20">
        <button
          onClick={() => navigate("/blog")}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:border-slate-400 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </button>
      </div>

      <Footer />
      </div>
    </>
  );
}
